import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import _xor from 'lodash/xor';
import qs from 'qs';
import { SubmissionError, getFormValues } from 'redux-form';

import intl from '../../shared/intl';
import endpoints from '../../../config/endpoints';
import http from '../../shared/http';
import createCalendar from '../../shared/calendar';
import createFilters from '../../shared/filters';
import createTable from '../components/table/unacceptable-conversions';
import { getTextFromList } from '../components/table/unacceptable-conversions';
import popups from '../components/popups';
import createWhitelistedIpsForm from '../components/whitelisted-ips-form';
import createBlacklistedIpsForm from '../components/blacklisted-ips-form';
import getRequestQuery from '../shared/get-request-query';
import checkPermissions from '../../shared/check-permissions';
import getFilterContext from '../shared/get-filters-context';
import csvExport from '../../shared/csvExport';

const whitelistedIpsForm = createWhitelistedIpsForm();
const blacklistedIpsForm = createBlacklistedIpsForm();

const calendar = createCalendar({
	namespace: 'unacceptableConversions',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'unacceptableConversions',
	stateNodeName: 'filters',
	needTakeInitialFiltersFromQuery: true,
	queryBuildFunc: getRequestQuery,
	listOfFilters: [{
		name: 'affiliates',
		title: 'Affiliates',
		placeholder: 'Add affiliates',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.AFFILIATES
		}
	}, {
		name: 'offers',
		title: 'Offers',
		placeholder: 'Add offers',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.OFFERS
		}
	}, {
		name: 'campaigns',
		title: 'Campaigns',
		placeholder: 'Add campaigns',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.CAMPAIGNS
		}
	}, {
		name: 'countries',
		title: 'Countries',
		placeholder: 'Add countries',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.LOCATION_CODES_WITH_TRAFFIC
		}
	}, {
		name: 'unacceptable_conversion_types',
		title: 'Unacceptable conversion types',
		placeholder: 'Enter types',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'static',
			source: 'root.lists.unacceptable-conversion-types'
		}
	}, {
		name: 'has_resended_conversion',
		title: 'Has resended conversion',
		placeholder: 'Select ',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'provided',
			source: [
				{ value: 'true', label: 'true' }
			]
		}
	}, ...['sid1', 'sid2', 'sid3', 'sid4', 'sid5'].map(sid => ({
		name: sid,
		title: sid,
		placeholder: `Add ${sid}`,
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'from_search'
		}
	}))]
});

const table = createTable({
	namespace: 'unacceptableConversions',
	stateNodeName: 'table'
});

// actions types
const FETCH_DATA_SUCCESS = 'unacceptable-conversions/fetch-data-success';
const FETCH_DATA_ERROR = 'unacceptable-conversions/fetch-data-error';

const RESEND_AUTOTRASH_OK = 'unacceptable-conversions/RESEND_AUTOTRASH_OK';
const RESEND_AUTOTRASH_NOT_OK = 'unacceptable-conversions/RESEND_AUTOTRASH_NOT_OK';

const SHOW_PROGRESS_BAR = 'unacceptable-conversions/show-progress-bar';
const CLEAR = 'unacceptable-conversions/clear';

const TOGGLE_CHECKED_ITEMS = 'unacceptable-conversions/TOGGLE_CHECKED_ITEMS';

// permissions
export const allowedPermissions = {
	'autotrash_view': 'autotrash_view',
	'resend': 'autotrash_resend'
};

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	calendar: calendar.initialState,
	filters: filters.initialState,
	table: table.initialState,
	checkedBan: [],
	query: {
		date_range: {
			start: null,
			end: null
		}
	},
	...popups.initialState,
	...whitelistedIpsForm.initialState,
	...blacklistedIpsForm.initialState,
	error: false
};

// actions
export const actions = Object.assign({
	fetchData: () => (dispatch) => {
		if (window.location.search.includes('is_skipped_default_fetchdata')) {
			return dispatch({ type: FETCH_DATA_SUCCESS, payload: { data: [] }});
		}
		dispatch(actions.fetchRequest());
	},
	resendConversionsList: (is_need_move_to_trash = false) => (dispatch, getState) => {
		const { checkedBan } =  getState().unacceptableConversions;
		const selectCancellationReasonForm = getFormValues('selectCancellationReasonForm')(getState());

		let query = `is_need_move_to_trash=${is_need_move_to_trash || ''}`;
		if (_get(selectCancellationReasonForm, 'cancellation_reason[0].value') && is_need_move_to_trash) {
			query += `&${qs.stringify({
				cancellation_reason: _get(selectCancellationReasonForm, 'cancellation_reason[0].value')
			})}`;
		}

		return http({
			url: `${endpoints.STATISTICS.RESEND_BANNED_LEADS}?${query}`,
			method: 'post',
			data: checkedBan,
			actions: [ RESEND_AUTOTRASH_OK, RESEND_AUTOTRASH_NOT_OK ]
		})(dispatch)
			.then((action) => {
				if (action.type === RESEND_AUTOTRASH_OK) {
					dispatch(actions.apply());
				}
			});
	},
	resendAutotrashConversion: (data, id, is_need_move_to_trash) => (dispatch) => {
		const url = `${endpoints.STATISTICS.RESENDED_AUTOTRASH}`.replace(':id', id);
		return http({
			url: `${url}?is_need_move_to_trash=${is_need_move_to_trash || ''}`,
			method: 'post',
			data: data,
			actions: [ RESEND_AUTOTRASH_OK, RESEND_AUTOTRASH_NOT_OK ]
		})(dispatch)
			.then((action) => {
				if (action.type === RESEND_AUTOTRASH_OK) {
					dispatch(popups.actions.toggleResendPopup());
					dispatch(actions.apply());
				} else {
					const respData = action.payload.response.data || {};
					const errorMessage = Array.isArray(respData)
						? respData.map(i => i.message).join(',')
						: respData.message;

					throw new SubmissionError({ _error: errorMessage });
				}
			});
	},

	fetchRequest: (externalQuery = {}) => (dispatch, getState) => {

		let query = getRequestQuery(
			getState().unacceptableConversions,
			externalQuery && externalQuery['query']
		);
		const selectCancellationReasonForm = getFormValues('selectCancellationReasonForm')(getState());

		if (selectCancellationReasonForm && selectCancellationReasonForm.is_showed_duplicates) {
			query += `&is_showed_duplicates=true`;
		}

		return http({
			url: `${endpoints.STATISTICS.UNACCEPTABLE_CONVERSIONS}?${query}`,
			method: 'get',
			actions: [
				FETCH_DATA_SUCCESS,
				FETCH_DATA_ERROR
			]
		})(dispatch);
	},
	exportCsv: () => (dispatch, getState) => {

		const additional_fields = [
			'name', 'phone', 'country_code', 'campaign_id', 'sid1', 'sid2', 'sid3', 'sid4', 'sid5'
		];
		const headers = [
			'affiliate', 'campaign', 'campaign_id', 'date', 'id', 'offer', 'offer_status', 'manager',
			'payload', 'type', 'validation_message', 'offer_id', 'affiliate_id', 'is_archived_campaign',
			...additional_fields
		];

		return actions.fetchRequest()(dispatch, getState)
			.then(action => {
				if (action.type === FETCH_DATA_SUCCESS && _get(action, 'payload.data.length')) {
					const rows = _get(action, 'payload.data').map(row => ({
						...row, type: getTextFromList(getState().root.lists['unacceptable-conversion-types'], row['type_id']),
						..._pick(row.payload, additional_fields)
					}));

					const columns = rows.map(entity => headers.map(index => entity[index] || '-'));

					const rowsForExport = [].concat([ headers ], columns);
					csvExport('Conversions_ban', rowsForExport);
				}
			});
	},

	apply: () => dispatch => {
		dispatch(actions.showProgressBar());
		dispatch(actions.fetchRequest());
	},

	selectAll: () => (dispatch, getState) => {
		const banTypes = getState().root.lists['unacceptable-conversion-types'];

		return selectors.getData(getState())
			.filter(item => getTextFromList(banTypes, item['type_id']) === 'spam_by_ip')
			.forEach(item => dispatch({ type: TOGGLE_CHECKED_ITEMS, payload: item.id }));

	},
	toggleCheckedItems: id => dispatch => dispatch({ type: TOGGLE_CHECKED_ITEMS, payload: id }),

	clear: () => dispatch =>  {
		dispatch(replace('statistics/unacceptable-conversions/'));
		dispatch({
			type: CLEAR
		});
	},

	showProgressBar: () => ({
		type: SHOW_PROGRESS_BAR
	})
}, calendar.actions,
filters.actions,
popups.actions,
whitelistedIpsForm.actions,
blacklistedIpsForm.actions,
table.actions
);

// reducers
const reducers = Object.assign({
	[LOCATION_CHANGE]: () => {
		return initialState;
	},
	[FETCH_DATA_SUCCESS]: (state, action) => ({
		...state, data: action.payload.data, showProgressBar: false, error: false
	}),
	[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
	[SHOW_PROGRESS_BAR]: state => ({...state, showProgressBar: true}),
	[SHOW_PROGRESS_BAR]: state => ({...state, showProgressBar: true}),
	[TOGGLE_CHECKED_ITEMS]: (state, action) =>
		({...state, checkedBan: [..._xor(state.checkedBan, [ action.payload ])]}),
	[CLEAR]: state => ({
		...initialState,
		query: {
			...initialState.query,
			currency: state.query.currency
		}
	})
},
calendar.reducers,
filters.reducers,
popups.reducers,
whitelistedIpsForm.reducers,
blacklistedIpsForm.reducers,
table.reducers
);
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.unacceptableConversions.data,
	getCheckedBan: state => state.unacceptableConversions.checkedBan,
	getIsDailyApprovePopupShowed: state => state.unacceptableConversions.isDailyApprovePopupShowed,
	getWhitelistedIps: state => state.unacceptableConversions.whitelistedIps,
	getBlacklistedIps: state => state.unacceptableConversions.blacklistedIps,
	getIsBlacklistedIpsPopupShowed: state => state.unacceptableConversions.isBlacklistedIpsPopupShowed,
	getResendConversionPopupAutoTrashId: popups.selectors.getResendConversionPopupAutoTrashId,
	getResendPopupAutotrashConversionData: popups.selectors.getResendPopupAutotrashConversionData,
	getIntl: intl,
	getTable: (state, usePagination) => table.selectors.getTable(state, usePagination),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.unacceptableConversions.calendar,
		state => state
	)(state),
	getFilters: state => filters.selectors.getFilters(
		state => state.unacceptableConversions.filters,
		state => state
	)(state),
	getProgressBar: state => state.unacceptableConversions.showProgressBar,
	getSearchInitialValues: state => ({
		search: state.unacceptableConversions.searchRefererText
	}),
	getSearchCrmIdInitialValues: state => ({
		search: state.unacceptableConversions.searchCrmIdText
	}),
	getFilterContext: state => getFilterContext(state.unacceptableConversions),
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.unacceptableConversions.error
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/statistics/unacceptable-conversions/index.js